import i18n from '@web/plugins/i18n'

import {
  MenuState,
  ItemMenu,
  Menu,
  MenuGettersTree
} from '@web/store/types/modules/menu'
import {
  isVideoConsultationSupport,
  guidelineUrl,
  instructionUrl,
  communityUrlFrontend
} from '@web/common/ExternalConfig/getters'
import { getUrlWithAuthParamsCommunity } from '@web/common/Utils'
import PAGE_NAME from '@web/consts/PAGE_NAME'

const state: MenuState = {
  home: {
    icon: '-course',
    name: String(i18n.global.t('myCourses')),
    link: {
      name: PAGE_NAME.HOME
    },
    note: '',
    type: 'router-link'
  },
  community: {
    icon: '-community',
    name: String(i18n.global.t('Community')),
    link: getUrlWithAuthParamsCommunity(communityUrlFrontend()),
    type: 'club'
  },
  course: {
    icon: '-course',
    name: String(i18n.global.t('myCourses')),
    link: {
      name: PAGE_NAME.COURSES
    },
    note: '',
    type: 'router-link'
  },
  chat: {
    icon: '-chat',
    name: String(i18n.global.t('messages')),
    link: {
      name: PAGE_NAME.CHATS
    },
    note: '',
    type: 'router-link'
  },
  homework: {
    icon: '-homework',
    name: String(i18n.global.t('homeworks')),
    link: {
      name: PAGE_NAME.HOMEWORKS
    },
    note: '',
    type: 'router-link'
  },
  videoConsultations: {
    icon: '-videoconsultations',
    name: String(i18n.global.t('videoConsultations.VideoConsultations')),
    link: {
      name: PAGE_NAME.VIDEO_CONSULTATIONS
    },
    note: '',
    type: 'router-link'
  },
  support: {
    icon: '-support',
    name: String(i18n.global.t('supportCenter')),
    link: { name: PAGE_NAME.SUPPORT },
    note: '',
    type: 'router-link'
  },
  edit: {
    icon: '-userEdit',
    name: String(i18n.global.t('editProfile')),
    link: {
      name: PAGE_NAME.EDIT_PROFILE
    },
    note: '',
    type: 'router-link'
  },
  settings: {
    icon: '-settings',
    name: String(i18n.global.t('Settings')),
    link: {
      name: PAGE_NAME.SETTINGS
    },
    note: '',
    type: 'router-link'
  },
  password: {
    icon: '-password',
    name: String(i18n.global.t('password')),
    link: {
      name: PAGE_NAME.USER_PASSWORD
    },
    note: '',
    type: 'router-link'
  },
  catalog: {
    icon: '-catalog',
    name: String(i18n.global.t('catalog')),
    link: {
      name: PAGE_NAME.CATALOG
    },
    note: '',
    type: 'router-link'
  },
  points: {
    icon: '-points',
    name: String(i18n.global.t('Points')),
    link: {
      name: PAGE_NAME.POINTS
    },
    type: 'router-link'
  },
  deleteMenu: {
    icon: '-delete',
    name: String(i18n.global.t('Delete')),
    link: {
      name: PAGE_NAME.DELETE
    },
    type: 'router-link'
  },
  exit: {
    icon: '-exit',
    name: String(i18n.global.t('accountExit')),
    link: {
      name: PAGE_NAME.LOGOUT
    },
    type: 'router-link'
  },
  subscriptions: {
    icon: '-subscriptions',
    name: String(i18n.global.t('Subscriptions')),
    link: {
      name: PAGE_NAME.SUBSCRIPTIONS
    },
    type: 'router-link'
  }
}

const getters: MenuGettersTree = {
  getSupport ({ support }, _1, _2, rootGetters) {
    return {
      ...support,
      name: rootGetters['user/getShowNewLk'] ? String(i18n.global.t('faq')) : String(i18n.global.t('supportCenter'))
    }
  },
  getVideoConsultations ({ videoConsultations }, _1, _2, rootGetters) {
    return rootGetters['user/getShowNewLk'] ? undefined : videoConsultations
  },
  getEditProfile ({ edit }, _1, _2, rootGetters) {
    return rootGetters['user/getShowNewLk'] ? undefined : edit
  },
  getPoints ({ points }, _1, _2, rootGetters) {
    return rootGetters['user/getShowNewLk'] ? undefined : points
  },
  getSocialNetworks ({ socialNetworks }, _1, _2, rootGetters) {
    return rootGetters['user/getShowNewLk'] ? undefined : socialNetworks
  },
  getClub ({ community }, _1, _2, rootGetters) {
    const _communities = rootGetters['community/communities']
      .filter(c => c.userCommunity)
      .filter(c => !c.chatOnly)

    if (!communityUrlFrontend() || !_communities.length) {
      return
    }

    return community
  },
  general ({ support, home, homework, chat, subscriptions }, { getVideoConsultations, getClub }, _, rootGetters) {
    const menu: Menu = {
      home,
      community: getClub,
      homework,
      chat,
      videoConsultations: getVideoConsultations,
      support,
      subscriptions
    }

    // const communities = rootGetters['community/communities']
    //   .filter(community => community.userCommunity)
    //   .filter(community => !community.chatOnly)

    // if (!communityUrlFrontend() || !communities.length) {
    //   delete menu.community
    // }

    for (const key in menu) {
      if (!menu[key]) {
        delete menu[key]
      }
    }

    return menu
  },

  setting ({ edit, points, settings, exit }, { getSocialNetworks, getVideoConsultations }, _, rootGetters) {
    const menu: Menu = {
      edit,
      points,
      settings,
      videoConsultations: getVideoConsultations
    }

    if (!guidelineUrl()) {
      menu.guideline = {
        icon: '-manual',
        name: String(i18n.global.t('UserManual')),
        link: 'guidelineUrl()',
        type: 'modal'
      }
    }

    if (instructionUrl()) {
      menu.instruction = {
        icon: '-instruction',
        name: String(i18n.global.t('instruction')),
        link: instructionUrl(),
        type: 'modal'
      }
    }

    if (rootGetters['assets/authCollection'].length && getSocialNetworks) {
      menu.socialNetworks = getSocialNetworks
    }

    menu.exit = exit

    for (const key in menu) {
      if (!menu[key]) {
        delete menu[key]
      }
    }

    return menu
  },

  mobile (_, { getSupport, general, setting }) {
    return {
      support: getSupport,
      ...general,
      ...setting
    }
  },
  mobileProfile ({ settings, catalog, deleteMenu }, { getSupport, getVideoConsultations, getEditProfile, getSocialNetworks }, _, rootGetters) {
    const menu: Menu = {
      edit: getEditProfile
        ? {
            ...getEditProfile,
            name: String(i18n.global.t('myInformation'))
          }
        : undefined,
      socialNetworks: getSocialNetworks,
      settings,
      videoConsultations: getVideoConsultations,
      support: getSupport,
      catalog,
      deleteMenu
    }

    for (const key in menu) {
      if (!menu[key]) {
        delete menu[key]
      }
    }

    if (!rootGetters['assets/authCollection'].length) {
      delete menu.socialNetworks
    }

    if (isVideoConsultationSupport()) {
      delete menu.videoConsultations
    }

    return menu
  }
}

export default {
  namespaced: true,
  state,
  getters
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any
